/**
 * Sirv 3D Viewer — lazy-loads sirv.js, handles variant changes.
 * Strategy: slide + thumbnail start HIDDEN. Only shown once Sirv renders successfully.
 * If Sirv fails or the spin file doesn't exist, nothing changes — product images stay as-is.
 */
(function () {
  'use strict';

  var SIRV_SCRIPT_URL = 'https://scripts.sirv.com/sirvjs/v3/sirv.js';
  var RENDER_CHECK_MS = 8000;
  var CHECK_INTERVAL_MS = 500;

  function findWrapper() {
    return document.querySelector('[id^="SirvViewer-"]');
  }

  function sectionIdFrom(wrapper) {
    return wrapper.id.replace('SirvViewer-', '');
  }

  function getElements(sectionId) {
    return {
      slide: document.getElementById('Slide-' + sectionId + '-sirv-3d'),
      thumb: document.getElementById('Slide-Thumbnails-' + sectionId + '-sirv-3d'),
      gallery: document.getElementById('Slider-Gallery-' + sectionId),
      thumbList: document.getElementById('Slider-Thumbnails-' + sectionId),
      galleryViewer: document.getElementById('GalleryViewer-' + sectionId),
      galleryThumbnails: document.getElementById('GalleryThumbnails-' + sectionId)
    };
  }

  /**
   * After showing/hiding slirv slides, tell the slider components to
   * re-index their slides so page tracking and slideChanged events
   * reference the correct elements.
   */
  function resetSliders(els) {
    if (els.galleryViewer && typeof els.galleryViewer.resetPages === 'function') {
      els.galleryViewer.resetPages();
    }
    if (els.galleryThumbnails && typeof els.galleryThumbnails.resetPages === 'function') {
      els.galleryThumbnails.resetPages();
    }
  }

  /**
   * Show the Sirv slide + thumbnail and make it the active first slide.
   */
  function showSirvSlide(wrapper) {
    var sectionId = sectionIdFrom(wrapper);
    var els = getElements(sectionId);
    if (!els.slide) return;

    // Show the slide and thumbnail
    els.slide.style.display = '';
    if (els.thumb) els.thumb.style.display = '';

    // Move Sirv to active, deactivate others
    if (els.gallery) {
      var allSlides = els.gallery.querySelectorAll('.slider__slide');
      for (var i = 0; i < allSlides.length; i++) {
        allSlides[i].classList.remove('is-active');
      }
      els.slide.classList.add('is-active');
      // Scroll gallery to show sirv slide
      els.gallery.scrollTo({ left: els.slide.offsetLeft });
    }

    // Update thumbnails — remove aria-current from ALL, add to Sirv
    if (els.thumbList && els.thumb) {
      var allBtns = els.thumbList.querySelectorAll('.thumbnail');
      for (var j = 0; j < allBtns.length; j++) {
        allBtns[j].removeAttribute('aria-current');
      }
      var sirvBtn = els.thumb.querySelector('.thumbnail');
      if (sirvBtn) sirvBtn.setAttribute('aria-current', 'true');
    }

    // Re-index slider components so they know about the new slide
    resetSliders(els);
  }

  /**
   * Hide the Sirv slide + thumbnail. Activate featured image instead.
   */
  function hideSirvSlide(wrapper) {
    var sectionId = sectionIdFrom(wrapper);
    var els = getElements(sectionId);

    if (els.slide) {
      els.slide.classList.remove('is-active');
      els.slide.style.display = 'none';
    }
    if (els.thumb) {
      var sirvBtn = els.thumb.querySelector('.thumbnail');
      if (sirvBtn) sirvBtn.removeAttribute('aria-current');
      els.thumb.style.display = 'none';
    }

    // Make sure a visible slide is active
    if (els.gallery) {
      var hasActive = els.gallery.querySelector('.slider__slide.is-active:not([style*="display: none"])');
      if (!hasActive) {
        var firstVisible = els.gallery.querySelector('.slider__slide:not([style*="display: none"])');
        if (firstVisible) firstVisible.classList.add('is-active');
      }
    }

    // Make sure a visible thumbnail is active
    if (els.thumbList) {
      var hasActiveThumb = els.thumbList.querySelector('.thumbnail-list__item:not([style*="display: none"]) .thumbnail[aria-current]');
      if (!hasActiveThumb) {
        var firstVisibleThumb = els.thumbList.querySelector('.thumbnail-list__item:not([style*="display: none"]) .thumbnail');
        if (firstVisibleThumb) firstVisibleThumb.setAttribute('aria-current', 'true');
      }
    }

    // Re-index slider components after removing the slide
    resetSliders(els);
  }

  function loadSirvScript(onSuccess, onError) {
    if (window.Sirv) { onSuccess(); return; }

    var existing = document.querySelector('script[src*="sirv.js"]');
    if (existing) {
      if (window.Sirv) { onSuccess(); return; }
      existing.addEventListener('load', onSuccess);
      existing.addEventListener('error', onError);
      return;
    }

    var s = document.createElement('script');
    s.src = SIRV_SCRIPT_URL;
    s.async = true;
    s.addEventListener('load', onSuccess);
    s.addEventListener('error', onError);
    document.head.appendChild(s);
  }

  function checkRendered(wrapper, onSuccess, onFail) {
    var sirvEl = wrapper.querySelector('.Sirv');
    if (!sirvEl) { onFail(); return; }

    var settled = false;
    function succeed() {
      if (settled) return;
      settled = true;
      onSuccess();
    }
    function fail() {
      if (settled) return;
      settled = true;
      onFail();
    }

    // Use Sirv's JavaScript API events if available
    if (window.Sirv && window.Sirv.on) {
      window.Sirv.on('viewer:ready', function (e) {
        // Check that the ready event is for OUR element
        if (e && e.target && (e.target === sirvEl || sirvEl.contains(e.target))) {
          succeed();
        }
      });
      window.Sirv.on('viewer:error', function (e) {
        if (e && e.target && (e.target === sirvEl || sirvEl.contains(e.target))) {
          fail();
        }
      });
    }

    // Fallback timeout — if no API event fires, check for actual spin content
    // Sirv's spin viewer creates a canvas element when successfully loaded
    var elapsed = 0;
    var check = setInterval(function () {
      elapsed += CHECK_INTERVAL_MS;
      if (settled) {
        clearInterval(check);
        return;
      }
      // Real spin content: canvas for WebGL rendering, or smv-viewer container
      var hasRealContent = sirvEl.querySelector('canvas') ||
                           sirvEl.querySelector('.smv-viewer') ||
                           sirvEl.querySelector('.sirv-spin-container canvas');
      // Error indicators: Sirv shows error messages or empty viewers
      var hasError = sirvEl.querySelector('.sirv-error') ||
                     sirvEl.querySelector('.smv-error');

      if (hasError) {
        clearInterval(check);
        fail();
      } else if (hasRealContent) {
        clearInterval(check);
        succeed();
      } else if (elapsed >= RENDER_CHECK_MS) {
        clearInterval(check);
        fail();
      }
    }, CHECK_INTERVAL_MS);
  }

  function init() {
    var wrapper = findWrapper();
    if (!wrapper) return;

    var sirvStarted = false;

    var startSirv = function () {
      if (sirvStarted) return;
      sirvStarted = true;

      if (window.Sirv && window.Sirv.start) {
        window.Sirv.start();
        checkRendered(wrapper,
          function () { showSirvSlide(wrapper); },
          function () { hideSirvSlide(wrapper); }
        );
      }
    };

    var handleError = function () {
      hideSirvSlide(wrapper);
    };

    // Lazy-load via IntersectionObserver — observe the GALLERY (visible), not the hidden wrapper
    var observeTarget = wrapper.closest('.product__media-wrapper') || wrapper.closest('media-gallery') || wrapper.parentElement;
    if ('IntersectionObserver' in window && observeTarget) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            loadSirvScript(startSirv, handleError);
            observer.unobserve(entry.target);
          }
        });
      }, { rootMargin: '400px' });
      observer.observe(observeTarget);
    } else {
      loadSirvScript(startSirv, handleError);
    }

    // Sirv error event
    document.addEventListener('sirv:error', function () {
      hideSirvSlide(wrapper);
    });

    // Variant change
    document.addEventListener('variant:changed', function (e) {
      var newSku = e.detail && e.detail.sku;
      if (!newSku || newSku === wrapper.dataset.sirvSku) return;

      var baseUrl = wrapper.dataset.sirvBaseUrl;
      var encodedSku = encodeURIComponent(newSku);
      var newUrl = baseUrl + encodedSku + '/' + encodedSku + '.spin';
      var sirvEl = wrapper.querySelector('.Sirv');

      if (sirvEl) {
        hideSirvSlide(wrapper);

        sirvEl.setAttribute('data-src', newUrl);
        wrapper.dataset.sirvSku = newSku;

        if (window.Sirv && window.Sirv.start) {
          window.Sirv.start();
          checkRendered(wrapper,
            function () { showSirvSlide(wrapper); },
            function () { hideSirvSlide(wrapper); }
          );
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
